// src/pages/ProductsPage/ProductsPage.jsx
import React, { useState, useEffect, useContext } from "react";
import { Container, Row, Col } from "react-bootstrap";
import { useLocation, useNavigate } from "react-router-dom";
import ProductCard from "../../../components/customer/cards/productCard/ProductCard";
import Pagination from "../../../components/general/pagination/Pagination";
import { ProductsPageSkeleton } from "../../../components/general/skeleton/SkeletonBlock";
import {
  FaSearch,
  FaLeaf,
  FaTh,
  FaChevronDown,
  FaChevronUp,
  FaFilter,
  FaBoxOpen,
  FaMapMarkerAlt,
} from "react-icons/fa";
import "../../../assets/css/customer/productPage/ProductsPage.css";
import MyNavbar from "../../../components/general/navbar/MyNavbar";
import PageHero from "../../../components/general/pageHero/PageHero";
import Footer from "../../../components/general/footer/Footer";
import Swal from "sweetalert2";
import axios from "axios";
import MyContext from "../../../context/MyContext";

const ProductsPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { baseUrl, baseUrlForFiles, loggedInCustomer, setCartProducts, authToken } = useContext(MyContext);

  const [products, setProducts] = useState([]);
  const [errorMessage, setErrorMessage] = useState("");

  const [loading, setLoading] = useState(true);

  const [viewMode, setViewMode] = useState("grid");
  const [sortBy, setSortBy] = useState("default");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [selectedSubcategory, setSelectedSubcategory] = useState("");
  const [openDropdown, setOpenDropdown] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(9);

  const [categoriesList, setCategoriesList] = useState([]);

  // ── viewAllCategories — authToken ke saath ──
  const viewAllCategories = () => {
    axios
      .get(`${baseUrl}/customer/category/viewAllCategories`)
      .then((response) => {
        if (response.data.hasOwnProperty("error") || response.data.success === false) {
          return;
        }
        setCategoriesList(response.data.categories || []);
      })
      .catch(() => {});
  };

  // ── viewAllProducts — city-lock hata di gayi hai (Intercity Bulk
  // Delivery spec), sab cities ke products aate hain ab ──
  const viewAllProducts = () => {
    setLoading(true);

    axios
      .get(`${baseUrl}/customer/product/viewAllProducts`)
      .then((response) => {
        setLoading(false);
        if (response.data.hasOwnProperty("error")) {
          setErrorMessage(response.data.error);
          Swal.fire({
            icon: "error",
            title: "Error",
            text: response.data.error,
          });
        } else {
          setProducts(response.data.product);
        }
      })
      .catch(() => {
        setLoading(false);
        setErrorMessage("Failed to load products.");
      });
  };

  const viewCartForContext = () => {
    if (!loggedInCustomer?.id) return;
    const formData = new FormData();

    axios
      .post(`${baseUrl}/customer/cart/viewAllCartProducts`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${authToken}`,
        },
      })
      .then((response) => {
        if (!response.data.hasOwnProperty("error") && response.data.success) {
          setCartProducts(response.data.cartProducts);
        }
      })
      .catch((error) => {
        console.log(error);
      });
  };

  useEffect(() => {
    viewAllCategories();
  }, []);

  useEffect(() => {
    viewAllProducts();
    viewCartForContext();
  }, [loggedInCustomer]);

  useEffect(() => {
    if (location.state?.category) {
      setSelectedCategory(location.state.category);
      setOpenDropdown("All");
      if (location.state?.subcategory) {
        setSelectedSubcategory(location.state.subcategory);
      }
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  useEffect(() => {
    setCurrentPage(1);
  }, [selectedCategory, selectedSubcategory, searchQuery, sortBy]);

  const getAllSubcategories = () => {
    const allSubs = [];
    categoriesList.forEach((parent) => {
      (parent.kmsubcategories || []).forEach((sub) =>
        allSubs.push({ name: sub.subcategoryname, parent: parent.categoryname }),
      );
    });
    return allSubs;
  };

  const getCurrentSubcategories = () => {
    if (selectedCategory === "All") return getAllSubcategories();
    const parent = categoriesList.find((c) => c.categoryname === selectedCategory);
    return (parent?.kmsubcategories || []).map((sub) => ({ name: sub.subcategoryname }));
  };

  const currentSubcategories = getCurrentSubcategories();

  const selectCategory = (name) => {
    setSelectedCategory(name);
    setSelectedSubcategory("");
  };

  const toggleAllDropdown = () => () => {
    setOpenDropdown((prev) => (prev === "All" ? null : "All"));
    selectCategory("All");
  };

  const pickCategory = (name) => () => selectCategory(name);
  const pickSubcategory = (subName) => () => setSelectedSubcategory(subName);

  const clearFilters = () => {
    setSearchQuery("");
    setSelectedCategory("All");
    setSelectedSubcategory("");
    setSortBy("default");
    setOpenDropdown(null);
    setCurrentPage(1);
  };

  const changeSearchQuery = (e) => setSearchQuery(e.target.value);
  const changeSortBy = (e) => setSortBy(e.target.value);
  const switchToGridView = () => setViewMode("grid");
  const changePage = (page) => setCurrentPage(page);
  const changeItemsPerPage = (val) => {
    setItemsPerPage(val);
    setCurrentPage(1);
  };
  const goToSettings = () => navigate("/customer/settings");

  const filteredProducts = products.filter((product) => {
    const matchesSearch = product.name
      .toLowerCase()
      .includes(searchQuery.toLowerCase());

    let matchesCategory = true;
    if (selectedSubcategory !== "") {
      matchesCategory = product.subcategory === selectedSubcategory;
    } else if (selectedCategory === "All") {
      matchesCategory = true;
    } else {
      const parentNode = categoriesList.find((c) => c.categoryname === selectedCategory);
      if (parentNode) {
        const childNames = (parentNode.kmsubcategories || []).map((c) => c.subcategoryname);
        matchesCategory = childNames.includes(product.subcategory);
      } else {
        matchesCategory = product.subcategory === selectedCategory;
      }
    }
    return matchesSearch && matchesCategory;
  });

  const sortedProducts = [...filteredProducts].sort((a, b) => {
    switch (sortBy) {
      case "price-low":
        return a.price - b.price;
      case "price-high":
        return b.price - a.price;
      case "rating":
        return b.rating - a.rating;
      case "popularity":
        return b.reviews - a.reviews;
      default:
        return 0;
    }
  });

  const indexOfLast = currentPage * itemsPerPage;
  const indexOfFirst = indexOfLast - itemsPerPage;
  const currentProducts = sortedProducts.slice(indexOfFirst, indexOfLast);

  return (
    <div className="products-page">
      <MyNavbar />
      <PageHero title="Our Products" breadcrumb="Products" />

      <Container>
          <Row>
            <Col lg={3} md={4} className="sidebar-col">
              <div className="products-sidebar">
                <div className="sidebar-widget">
                  <div className="widget-header">
                    <FaSearch className="widget-icon" />
                    <h3 className="sidebar-title">Search</h3>
                  </div>
                  <div className="sidebar-search">
                    <button className="sidebar-search-icon">
                      <FaSearch />
                    </button>
                    <input
                      type="text"
                      placeholder="Search products..."
                      value={searchQuery}
                      onChange={changeSearchQuery}
                      className="sidebar-search-input"
                    />
                  </div>
                </div>

                <div className="sidebar-widget">
                  <div className="widget-header">
                    <FaFilter className="widget-icon" />
                    <h3 className="sidebar-title">Categories</h3>
                  </div>
                  <div className="cat-section">
                    <button
                      className={`cat-dropdown-btn ${selectedCategory === "All" && !openDropdown ? "active" : ""} ${openDropdown === "All" ? "active" : ""}`}
                      onClick={toggleAllDropdown()}
                    >
                      <FaBoxOpen className="cat-btn-icon" />
                      <span>All</span>
                      {openDropdown === "All" ? (
                        <FaChevronUp className="cat-chevron" />
                      ) : (
                        <FaChevronDown className="cat-chevron" />
                      )}
                    </button>

                    {openDropdown === "All" && (
                      <div className="cat-dropdown-content">
                        {categoriesList.map((cat) => (
                          <button
                            key={cat.id}
                            className={`cat-parent-btn ${selectedCategory === cat.categoryname ? "active" : ""}`}
                            onClick={pickCategory(cat.categoryname)}
                          >
                            <FaBoxOpen className="cat-btn-icon" />
                            <span>{cat.categoryname}</span>
                          </button>
                        ))}
                      </div>
                    )}

                    {currentSubcategories.length > 0 && (
                      <div className="cat-radio-group">
                        <p className="cat-subcategory-heading">
                          <FaFilter className="cat-heading-icon" />
                          Subcategories
                        </p>
                        {currentSubcategories.map((sub) => (
                          <label key={sub.name} className="cat-radio-label">
                            <input
                              type="radio"
                              name="subcategory"
                              checked={selectedSubcategory === sub.name}
                              onChange={pickSubcategory(sub.name)}
                              className="cat-radio-input"
                            />
                            <span className="cat-radio-custom"></span>
                            <span className="cat-radio-text">{sub.name}</span>
                          </label>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                <button className="sidebar-clear-btn" onClick={clearFilters}>
                  Clear All Filters
                </button>
              </div>
            </Col>

            <Col lg={9} md={8} className="content-col">
              <div className="products-toolbar">
                <div className="toolbar-left">
                  <div className="view-toggle">
                    <button
                      className={`view-btn ${viewMode === "grid" ? "active" : ""}`}
                      onClick={switchToGridView}
                    >
                      <FaTh />
                    </button>
                  </div>
                  <p className="results-count">
                    Showing {sortedProducts.length === 0 ? 0 : indexOfFirst + 1}–
                    {Math.min(indexOfLast, sortedProducts.length)} of{" "}
                    {sortedProducts.length} results
                  </p>
                </div>
                <div className="toolbar-right">
                  <div className="sort-dropdown">
                    <select
                      value={sortBy}
                      onChange={changeSortBy}
                      className="sort-select"
                    >
                      <option value="default">Default sorting</option>
                      <option value="price-low">Price: Low to High</option>
                      <option value="price-high">Price: High to Low</option>
                      <option value="popularity">Sort by popularity</option>
                      <option value="rating">Sort by rating</option>
                    </select>
                    <FaChevronDown className="dropdown-icon" />
                  </div>
                </div>
              </div>

              <Row className={`products-grid ${viewMode}`}>
                {loading ? (
                  <Col xs={12}>
                    <ProductsPageSkeleton />
                  </Col>
                ) : currentProducts.length > 0 ? (
                  currentProducts?.map((product, index) => (
                    <Col
                      lg={4}
                      md={6}
                      sm={12}
                      key={product.id}
                      className="product-col"
                    >
                      <ProductCard
                        product={product}
                        index={index}
                        baseUrlForFiles={baseUrlForFiles}
                      />
                    </Col>
                  ))
                ) : (
                  <Col xs={12}>
                    <div className="no-products">
                      <FaLeaf className="no-products-icon" />
                      <h3>No products found</h3>
                      <p>
                        {searchQuery ||
                        selectedCategory !== "All" ||
                        selectedSubcategory
                          ? "Try adjusting your filters or search query."
                          : "No products available right now."}
                      </p>
                      {(searchQuery ||
                        selectedCategory !== "All" ||
                        selectedSubcategory) && (
                        <button className="reset-filters-btn" onClick={clearFilters}>
                          Reset Filters
                        </button>
                      )}
                    </div>
                  </Col>
                )}
              </Row>

              {!loading && sortedProducts.length > 0 && (
                <Pagination
                  totalItems={sortedProducts.length}
                  itemsPerPage={itemsPerPage}
                  currentPage={currentPage}
                  onPageChange={changePage}
                  onItemsPerPageChange={changeItemsPerPage}
                />
              )}
            </Col>
          </Row>
      </Container>
      <Footer />
    </div>
  );
};

export default ProductsPage;
